<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";
import { mapState } from "vuex";
import { required } from "vuelidate/lib/validators";
import Banners from "@/components/widgets/banners";
import Stat from "@/components/widgets/stat";
import ChartGraduation from "@/components/widgets/chart-graduation.vue";
import ChartBonus from "@/components/widgets/chart-bonus";
import ChartPoints from "@/components/widgets/chart-points";
import ChartOrders from "@/components/widgets/chart-orders";
import Star72 from "@/components/star72.vue";
import GraduationProgress from "@/components/graduation-progress.vue";
import Pro from "@/components/pro.vue";
import EvolutionCompact from "@/components/evolution-compact.vue";
import Binary from "@/components/binary.vue";

export default {
  locales: {
    pt: {
      'Combo': 'Combo',
      'Status': 'Status',
      'Current Graduation': 'Graduação Atual',
      'Graduation': 'Graduação',
      'Profit Sharing': 'Participação de Lucros',
      'Total Bonus': 'Total de Bônus',
      'Available Balance': 'Saldo Disponível',
      'Founder': 'Fundador',
      'You left some products in your cart, click here to complete your order!': 'Você deixou alguns produtos em seu carrinho, clique aqui para finalizar o seu pedido!',
      'Referral Link': 'Link de indicação',
      'E-commerce Link': 'Link do e-commerce',
      'Copied!': 'Copiado!',
      'Qualified': 'Qualificado',
      'Not Qualified': 'Não Qualificado',
      'L. Left': 'L. Esquerdo',
      'L. Right': 'L. Direito',
      'COEFFICIENT': 'COEFICIENTE',
      'Percent': 'Porcentagem',
      'Bonus': 'Bônus',
      'Bonuses': 'Bonificações',
      'Points': 'Pontuações',
      'Orders': 'Pedidos',
      'Name': 'Nome',
      'City': 'Cidade',
      'State': 'Estado',
      'Country': 'País',
      'Brazil': 'Brasil',
      'Paraguay': 'Paraguai',
      'Save': 'Salvar',
      'Download': 'Baixar',
      'Change': 'Alterar',
      'Data saved successfully.': 'Dados salvos com sucesso.',
      'Fill in all fields and save to release the download.': 'Preencha todos os campos e salve para liberar o download.',
    },
    es: {
      'Combo': 'Combo',
      'Status': 'Estado',
      'Graduation': 'Graduación',
      'Current Graduation': 'Graduación Actual',
      'Profit Sharing': 'Participación de Utilidades',
      'Total Bonus': 'Total de Bono',
      'Available Balance': 'Saldo Disponible',
      'Founder': 'Fundador',
      'You left some products in your cart, click here to complete your order!': 'Dejaste algunos productos en tu carrito, ¡haz clic aquí para finalizar tu pedido!',
      'Referral Link': 'Enlace de Referido',
      'E-commerce Link': 'Enlace de E-commerce',
      'Copied!': '¡Copiado!',
      'Qualified': 'Calificado',
      'Not Qualified': 'No Calificado',
      'L. Left': 'L. Izquierdo',
      'L. Right': 'L. Derecho',
      'COEFFICIENT': 'COEFICIENTE',
      'Percent': 'Porcentagem',
      'Bonus': 'Bono',
      'Bonuses': 'Bonificaciones',
      'Points': 'Puntuaciones',
      'Orders': 'Pedidos',
      'Name': 'Nombre',
      'City': 'Ciudad',
      'State': 'Estado',
      'Country': 'País',
      'Brazil': 'Brasil',
      'Paraguay': 'Paraguay',
      'Save': 'Guardar',
      'Download': 'Descargar',
      'Change': 'Cambiar',
      'Data saved successfully.': 'Datos guardados con éxito.',
      'Fill in all fields and save to release the download.': 'Completa todos los campos y guarda para liberar la descarga.',
    }
  },
  components: {
    Layout,
    Banners,
    Stat,
    ChartGraduation,
    ChartBonus,
    ChartPoints,
    ChartOrders,
    Star72,
    GraduationProgress,
    Pro,
    EvolutionCompact,
    Binary
  },
  computed: {
    ...mapState({
      account: state => state.account
    }),
    // Colunas de stat cards por linha, conforme os breakpoints usados no v-for (col-6 / col-md-6 / col-lg-4)
    statItemsPerRow() {
      if (this.windowWidth >= 1025) {
        return 3
      }
      return 2
    },
    // Card da graduação fica empilhado (sem limite de altura) abaixo de 768px, então não há necessidade de dividir os cards
    isGraduationStacked() {
      return this.windowWidth < 768
    },
    // Quantidade de stat cards que cabem ao lado da imagem da graduação (limitada a ~3 linhas, ver .card-graduation max-height)
    primaryStatData() {
      if (this.isGraduationStacked || !this.statData) {
        return this.statData || []
      }
      return this.statData.slice(0, this.statItemsPerRow * 3)
    },
    // Restante dos stat cards, exibidos em uma linha de largura total abaixo do bloco da graduação
    secondaryStatData() {
      if (this.isGraduationStacked || !this.statData) {
        return []
      }
      return this.statData.slice(this.statItemsPerRow * 3)
    },
    storyIsDirty() {
      return JSON.stringify(this.storyModal.form) !== JSON.stringify(this.storyModal.original)
    },
    hasStoryData() {
      const o = this.storyModal.original
      return !!o.name && !!o.city && !!o.state && !!o.country
    },
    canDownloadStory() {
      const f = this.storyModal.form
      return !!f.name && !!f.city && !!f.state && !!f.country && !this.storyIsDirty
    },
    storyCountryName() {
      const option = this.storyModal.countryOptions.find(o => o.abbr === this.storyModal.original.country)
      return option ? this.t(option.name) : this.storyModal.original.country
    },
  },
  data() {
    return {
      monthly: "inactive",

      visibility: {
        graduation: true,
        bonus: true,
      },

      points: {
        binary: '-',
        unilevel: '-',
        graduation: '-',
        premiation: '-',
      },

      since: '',

      total: {
        bonus: {
          participacao: "R$ -,--",
          month: "R$ -,--",
          total: "R$ -,--",
        },
        balance: "R$ -,--",
        network: {
          inactives: '0',
          actives: '0',
          total: '0',
          month: '0',
          growth: '0',
        },
        points: {
          binary: 0,
          unilevel: 0,
          graduation: 0,
        },
      },

      graduation: {
        id: null,
        name: '-',
        image: null,
        points: '0',
        background: null,
        loadingImage: false,
      },

      statData: null,
      wallets: null,

      windowWidth: window.innerWidth,

      cart: {
        items: {
          loading: true,
          total: 0,
          list: null
        },
        points: {
          binary: 0,
          unilevel: 0,
          graduation: 0,
        },
        subtotal: '0.00',
        sale: '0.00',
        shipping: {
          method: '-',
          value: '-',
        },
        discount: '0.00',
        total: '0.00'
      },

      referral: {
        sponsored: process.env.VUE_APP_URL_PAINEL + this.$store.state.account.user.username,
        ecommerce: 'https://' + this.$store.state.account.user.username + '.' + process.env.VUE_APP_URL_ECOMMERCE,
      },

      storyModal: {
        loading: false,
        saving: false,
        downloading: false,
        editing: true,
        countryOptions: [
          { abbr: 'BR', name: 'Brazil' },
          { abbr: 'PY', name: 'Paraguay' },
        ],
        form: { name: '', city: '', state: '', country: '' },
        original: { name: '', city: '', state: '', country: '' },
      },
    };
  },
  validations: {
    storyModal: {
      form: {
        name: { required },
        city: { required },
        state: { required },
        country: { required },
      }
    }
  },
  methods: {
    getMonthly() {
      api.get("reports/monthly").then((response) => {
        this.monthly = response.data.status;
        this.getStatData();
      });
    },
    getNetwork() {
      api
        .get('reports/network')
        .then(response => {
          if (response.data.status == 'success') {
            this.total.network.inactives = response.data.inactives;
            this.total.network.actives = response.data.actives;
            this.total.network.total = response.data.total;
            this.total.network.month = response.data.month;
            this.total.network.growth = response.data.growth;
            this.getStatData();
          }
        })
    },
    getPoints() {
      api
        .get('reports/points')
        .then(response => {
          if (response.data.status == 'success') {
            this.total.points = response.data.total
            this.getStatData();
          }
        })
    },
    getGraduation() {
      api
        .get('reports/graduation')
        .then(response => {
          if (response.data.status == 'success') {
            this.graduation.id = response.data.permanent.id
            this.graduation.name = response.data.permanent.name
            this.graduation.image = response.data.permanent.image
            this.graduation.points = response.data.graduation.points
            this.getStatData();
          }
        })
    },
    getGraduationImage() {
      const cached = localStorage.getItem('graduation_image')
      if (cached) {
        this.graduation.background = cached
        return
      }

      this.graduation.loadingImage = true

      api
        .get('reports/graduation/image')
        .then(response => {
          if (response.data.status == 'success') {
            this.graduation.background = response.data.image
            localStorage.setItem('graduation_image', response.data.image)
          }
        })
        .finally(() => {
          this.graduation.loadingImage = false
        })
    },
    getParticipacao() {
      api.get("reports/participacao").then((response) => {
        if (response.data.status == "success") {
          this.total.bonus.participacao = this.$options.filters.currency(
            response.data.prediction.bonus
          );
          this.getStatData();
        }
      });
    },
    getTotalBonus() {
      api
        .get('reports/bonus/total')
        .then(response => {
          if (response.data.status == 'success') {
            this.total.bonus.total = this.$options.filters.currency(response.data.total)
            this.getStatData();
          }
        })
    },
    getTotalBonusMonth() {
      api
        .get('reports/bonus')
        .then(response => {
          if (response.data.status == 'success') {
            if (response.data.since.status == 'active') {
              this.since = response.data.since.calc
            }
            this.total.bonus.month = this.$options.filters.currency(response.data.total)
            this.getStatData();
          }
        })
    },
    getTotalBalance() {
      api
        .get('wallet/balance/network')
        .then(response => {
          if (response.data.status == 'success') {
            this.total.balance = this.$options.filters.currency(response.data.balance)
            this.getStatData();
          }
        })
    },
    getStatData() {
      this.statData = [
        {
          type: "square",
          title: "Combo",
          value: this.account.user.package.name,
          icon: "bx-package"
        },
        {
          type: "square",
          icon: "bx-time-five",
          title: "Status",
          value: this.monthly,
          month: true,
        },
        {
          type: "square",
          icon: "bxs-graduation",
          title: "Current Graduation",
          value: this.graduation.name,
        },
        {
          type: "square",
          icon: "bx-line-chart",
          title: "Profit Sharing",
          value: this.total.bonus.participacao,
          month: true,
        },
        {
          type: "square",
          icon: "bx-dollar",
          title: "Total Bonus",
          value: this.total.bonus.total,
          extra: this.since,
        },
        {
          type: "square",
          icon: "bx-wallet",
          title: "Available Balance",
          value: this.total.balance,
        },
        {
          type: "square",
          icon: "bx-user",
          title: "Ativos/Inativos",
          value: this.total.network.actives + '/' + this.total.network.inactives,
          extra: this.total.network.total,
        },
        {
          type: "square",
          icon: "bx-user-plus",
          title: "Novos Cadastros",
          value: this.total.network.month,
          month: true,
        },
        {
          type: "square",
          icon: "bx-bar-chart-alt",
          title: "Crescimento",
          value: this.total.network.growth,
          month: true,
        }
      ]
    },
    getCart() {
      if (!localStorage.cart) {
        api
          .get('store/cart')
          .then(response => {
            if (response.data.status == 'success') {
              this.setStorage(response.data)
            }
          })
          .catch(error => {
            this.cart.errored = error
          })
      } else {
        this.cart = JSON.parse(localStorage.cart)
      }
    },
    setStorage(data) {
      this.cart.items.total = data.items.total
      this.cart.items.list = data.items.list
      this.cart.subtotal = data.subtotal
      this.cart.sale = data.sale
      this.cart.shipping = data.shipping
      this.cart.discount = data.discount
      this.cart.total = data.total

      localStorage.cart = JSON.stringify(this.cart)
    },
    updateWindowWidth() {
      this.windowWidth = window.innerWidth
    },
    openStoryModal() {
      this.$bvModal.show('graduationStoryModal')
      this.getStoryData()
    },
    getStoryData() {
      this.storyModal.loading = true
      api
        .get('reports/graduation/story')
        .then(response => {
          if (response.data.status == 'success') {
            this.storyModal.form = { ...response.data.user }
            this.storyModal.original = { ...response.data.user }
          }
          this.storyModal.editing = !this.hasStoryData
        })
        .finally(() => {
          this.storyModal.loading = false
        })
    },
    saveStoryData() {
      this.$v.storyModal.form.$touch()
      if (this.$v.storyModal.form.$invalid) {
        return
      }

      this.storyModal.saving = true

      api
        .post('reports/graduation/story', {
          name: this.storyModal.form.name,
          city: this.storyModal.form.city,
          state: this.storyModal.form.state,
          country: this.storyModal.form.country,
        })
        .then(response => {
          if (response.data.status == 'success') {
            this.storyModal.original = { ...this.storyModal.form }
            this.storyModal.editing = false
            this.$toast.success(this.t(response.data.message))
          } else {
            this.$toast.error(this.t(response.data.message))
          }
        })
        .finally(() => {
          this.storyModal.saving = false
        })
    },
    editStory() {
      this.storyModal.editing = true
    },
    downloadStory() {
      if (!this.canDownloadStory) {
        return
      }

      this.storyModal.downloading = true

      api
        .post('reports/graduation/story/download', {}, { responseType: 'blob' })
        .then(response => {
          let filename = 'graduation-story.png'
          const disposition = response.headers['content-disposition']
          if (disposition) {
            const match = disposition.match(/filename="?([^"]+)"?/)
            if (match && match[1]) {
              filename = match[1]
            }
          }

          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
        .finally(() => {
          this.storyModal.downloading = false
        })
    },
  },
  mounted() {
    this.getMonthly()
    this.getNetwork()
    this.getPoints()
    this.getGraduation()
    this.getGraduationImage()
    this.getParticipacao()
    this.getTotalBonus()
    this.getTotalBonusMonth()
    this.getTotalBalance()
    this.getStatData()
    this.getCart()

    window.addEventListener('resize', this.updateWindowWidth)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowWidth)
  },
};
</script>

<template>
  <Layout>
    <router-link v-if="cart.items.total > 0" to="/store/cart" class="text-dark">
      <div class="card">
        <div class="card-body bg-soft-warning p-0">
          <div class="d-flex align-items-center">
            <img class="rounded-circle header-profile-user m-2 mx-2" :src="cart.items.list[Object.keys(cart.items.list)[0]].image" width="30px" height="30px">
            <div class="py-3">{{ t('You left some products in your cart, click here to complete your order!') }}</div>
          </div>
        </div>
      </div>
    </router-link>

    <Banners></Banners>

    <div class="card">
      <div class="card-body p-3 d-flex justify-content-between align-items-center">
        <div class="mr-2">
          <div class="bg-light rounded-circle p-2">
            <i class="bx bx-link-alt font-size-24 align-middle"></i>
          </div>
        </div>
        <h5 class="flex-fill m-0 align-items-center font-size-16">
          {{ t('Referral Link') }}
        </h5>
        <div class="d-flex align-items-center">
          <div class="ml-1">
            <ShareNetwork network="whatsapp" url="" :title="referral.sponsored">
              <div class="bg-light rounded-circle p-2"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: #1C1C1E;">
                  <path fill-rule="evenodd" clip-rule="evenodd"
                    d="M18.403 5.633A8.919 8.919 0 0 0 12.053 3c-4.948 0-8.976 4.027-8.978 8.977 0 1.582.413 3.126 1.198 4.488L3 21.116l4.759-1.249a8.981 8.981 0 0 0 4.29 1.093h.004c4.947 0 8.975-4.027 8.977-8.977a8.926 8.926 0 0 0-2.627-6.35m-6.35 13.812h-.003a7.446 7.446 0 0 1-3.798-1.041l-.272-.162-2.824.741.753-2.753-.177-.282a7.448 7.448 0 0 1-1.141-3.971c.002-4.114 3.349-7.461 7.465-7.461a7.413 7.413 0 0 1 5.275 2.188 7.42 7.42 0 0 1 2.183 5.279c-.002 4.114-3.349 7.462-7.461 7.462m4.093-5.589c-.225-.113-1.327-.655-1.533-.73-.205-.075-.354-.112-.504.112s-.58.729-.711.879-.262.168-.486.056-.947-.349-1.804-1.113c-.667-.595-1.117-1.329-1.248-1.554s-.014-.346.099-.458c.101-.1.224-.262.336-.393.112-.131.149-.224.224-.374s.038-.281-.019-.393c-.056-.113-.505-1.217-.692-1.666-.181-.435-.366-.377-.504-.383a9.65 9.65 0 0 0-.429-.008.826.826 0 0 0-.599.28c-.206.225-.785.767-.785 1.871s.804 2.171.916 2.321c.112.15 1.582 2.415 3.832 3.387.536.231.954.369 1.279.473.537.171 1.026.146 1.413.089.431-.064 1.327-.542 1.514-1.066.187-.524.187-.973.131-1.067-.056-.094-.207-.151-.43-.263">
                  </path>
                </svg>
              </div>
            </ShareNetwork>
          </div>
          <div class="ml-1">
            <button class="btn btn-link p-0 m-0" v-clipboard:copy="referral.sponsored
              " v-on:click="$toast.success(t('Copied!'));">
              <div class="bg-light rounded-circle p-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: #1C1C1E;">
                  <path
                    d="M20 2H10c-1.103 0-2 .897-2 2v4H4c-1.103 0-2 .897-2 2v10c0 1.103.897 2 2 2h10c1.103 0 2-.897 2-2v-4h4c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM4 20V10h10l.002 10H4zm16-6h-4v-4c0-1.103-.897-2-2-2h-4V4h10v10z">
                  </path>
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="d-none card">
      <div class="card-body p-1 d-flex justify-content-between align-items-center">
        <div class="mr-2">
          <div class="bg-light rounded-circle p-2">
            <i class="bx bx-link-alt font-size-24 align-middle"></i>
          </div>
        </div>
        <h5 class="flex-fill m-0 align-items-center font-size-16">
          {{ t('E-commerce Link') }}
        </h5>
        <div class="d-flex align-items-center">
          <div class="ml-1">
            <ShareNetwork network="whatsapp" url="" :title="referral.ecommerce">
              <div class="bg-light rounded-circle p-2"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: #1C1C1E;">
                  <path fill-rule="evenodd" clip-rule="evenodd"
                    d="M18.403 5.633A8.919 8.919 0 0 0 12.053 3c-4.948 0-8.976 4.027-8.978 8.977 0 1.582.413 3.126 1.198 4.488L3 21.116l4.759-1.249a8.981 8.981 0 0 0 4.29 1.093h.004c4.947 0 8.975-4.027 8.977-8.977a8.926 8.926 0 0 0-2.627-6.35m-6.35 13.812h-.003a7.446 7.446 0 0 1-3.798-1.041l-.272-.162-2.824.741.753-2.753-.177-.282a7.448 7.448 0 0 1-1.141-3.971c.002-4.114 3.349-7.461 7.465-7.461a7.413 7.413 0 0 1 5.275 2.188 7.42 7.42 0 0 1 2.183 5.279c-.002 4.114-3.349 7.462-7.461 7.462m4.093-5.589c-.225-.113-1.327-.655-1.533-.73-.205-.075-.354-.112-.504.112s-.58.729-.711.879-.262.168-.486.056-.947-.349-1.804-1.113c-.667-.595-1.117-1.329-1.248-1.554s-.014-.346.099-.458c.101-.1.224-.262.336-.393.112-.131.149-.224.224-.374s.038-.281-.019-.393c-.056-.113-.505-1.217-.692-1.666-.181-.435-.366-.377-.504-.383a9.65 9.65 0 0 0-.429-.008.826.826 0 0 0-.599.28c-.206.225-.785.767-.785 1.871s.804 2.171.916 2.321c.112.15 1.582 2.415 3.832 3.387.536.231.954.369 1.279.473.537.171 1.026.146 1.413.089.431-.064 1.327-.542 1.514-1.066.187-.524.187-.973.131-1.067-.056-.094-.207-.151-.43-.263">
                  </path>
                </svg>
              </div>
            </ShareNetwork>
          </div>
          <div class="ml-1">
            <button class="btn btn-link p-0 m-0" v-clipboard:copy="referral.ecommerce
              " v-on:click="$toast.success(t('Copied!'));">
              <div class="bg-light rounded-circle p-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: #1C1C1E;">
                  <path
                    d="M20 2H10c-1.103 0-2 .897-2 2v4H4c-1.103 0-2 .897-2 2v10c0 1.103.897 2 2 2h10c1.103 0 2-.897 2-2v-4h4c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM4 20V10h10l.002 10H4zm16-6h-4v-4c0-1.103-.897-2-2-2h-4V4h10v10z">
                  </path>
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Star72 />
      </div>
    </div>

    <div class="d-flex flex-wrap home-graduation-row">
      <div class="home-graduation-col">
        <div
          class="card card-graduation"
          :class="{ 'skeleton-pulse': graduation.loadingImage }"
          :style="graduation.background ? { backgroundImage: 'url(' + graduation.background + ')' } : {}"
        >
          <div class="card-body d-flex flex-column h-100">
            <div class="reference-month">{{ this.$moment().format('MM/YYYY') }}</div>
            <a class="story-download-toggle d-none" @click="openStoryModal">
              <i class="mdi mdi-download"></i>
            </a>
            <div class="row mt-auto">
              <div class="col-6">
                <fieldset class="card-glass text-center flex-fill mt-3 mb-0 p-2">
                  <h5 class="mb-1 text-white graduation-value" :class="{ 'value-hidden': !visibility.graduation }">{{ graduation.points | points }} PG</h5>
                  <div class="bg-dark rounded py-1 text-uppercase text-white font-weight-medium graduation-label position-relative">
                    Graduação
                    <a class="value-toggle" @click="visibility.graduation = !visibility.graduation">
                      <i :class="visibility.graduation ? 'mdi mdi-eye' : 'mdi mdi-eye-off'"></i>
                    </a>
                  </div>
                </fieldset>
              </div>
              <div class="col-6">
                <fieldset class="card-glass text-center flex-fill mt-3 mb-0 p-2">
                  <h5 class="mb-1 text-white graduation-value" :class="{ 'value-hidden': !visibility.bonus }">{{ total.bonus.month }}</h5>
                  <div class="bg-dark rounded py-1 text-uppercase text-white font-weight-medium graduation-label position-relative">
                    Bônus
                    <a class="value-toggle" @click="visibility.bonus = !visibility.bonus">
                      <i :class="visibility.bonus ? 'mdi mdi-eye' : 'mdi mdi-eye-off'"></i>
                    </a>
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="home-stats-col">
        <div class="row">
          <div v-for="(stat, index) of primaryStatData" :key="index" class="col-6 col-md-6 col-lg-4">
            <Stat :type="stat.type" :icon="stat.icon" :title="stat.title" :value="stat.value" :month="stat.month" :extra="stat.extra" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="secondaryStatData.length > 0" class="row">
      <div v-for="(stat, index) of secondaryStatData" :key="index" class="col-6 col-md-4 col-lg-3">
        <Stat :type="stat.type" :icon="stat.icon" :title="stat.title" :value="stat.value" :month="stat.month" :extra="stat.extra" />
      </div>
    </div>

    <b-modal id="graduationStoryModal" hide-footer :title="t('Graduation')">
      <div v-if="!storyModal.editing">
        <div class="story-summary">
          <div class="story-summary-icon">
            <i class="bx bx-check"></i>
          </div>
          <div class="story-summary-text">
            <div class="story-summary-name">{{ storyModal.original.name }}</div>
            <div class="story-summary-location">{{ storyModal.original.city }}, {{ storyModal.original.state }} · {{ storyCountryName }}</div>
          </div>
        </div>

        <b-button variant="outline-secondary" block @click="editStory">
          {{ t('Change') }}
        </b-button>
        <hr>
        <b-button variant="default" block :disabled="storyModal.downloading" @click="downloadStory">
          {{ t('Download') }}
          <b-spinner v-if="storyModal.downloading" small class="ml-2 align-middle" variant="secondary" role="status"></b-spinner>
        </b-button>
      </div>

      <b-form v-else @submit.prevent="saveStoryData">
        <b-form-group id="story-name" :label="t('Name')" label-for="story-name">
          <b-form-input id="story-name" v-model="storyModal.form.name" :state="$v.storyModal.form.name.$dirty ? !$v.storyModal.form.name.$invalid : null" @input="$v.storyModal.form.name.$touch()"></b-form-input>
        </b-form-group>
        <b-form-group id="story-city" :label="t('City')" label-for="story-city">
          <b-form-input id="story-city" v-model="storyModal.form.city" :state="$v.storyModal.form.city.$dirty ? !$v.storyModal.form.city.$invalid : null" @input="$v.storyModal.form.city.$touch()"></b-form-input>
        </b-form-group>
        <b-form-group id="story-state" :label="t('State')" label-for="story-state">
          <b-form-input id="story-state" v-model="storyModal.form.state" :state="$v.storyModal.form.state.$dirty ? !$v.storyModal.form.state.$invalid : null" @input="$v.storyModal.form.state.$touch()"></b-form-input>
        </b-form-group>
        <b-form-group id="story-country" :label="t('Country')" label-for="story-country">
          <select
            id="story-country"
            v-model="storyModal.form.country"
            class="custom-select"
            :class="{ 'is-invalid': $v.storyModal.form.country.$dirty && $v.storyModal.form.country.$invalid }"
            @change="$v.storyModal.form.country.$touch()"
          >
            <option value="" disabled>{{ t('Country') }}</option>
            <option v-for="option in storyModal.countryOptions" :key="option.abbr" :value="option.abbr">{{ t(option.name) }}</option>
          </select>
        </b-form-group>
        <b-button variant="default" type="submit" block :disabled="storyModal.saving">
          {{ t('Save') }}
          <b-spinner v-if="storyModal.saving" small class="ml-2 align-middle" variant="secondary" role="status"></b-spinner>
        </b-button>
      </b-form>
    </b-modal>

    <div class="row">
      <div class="col-12">
        <GraduationProgress />
      </div>
    </div>

    <div class="row d-none">
      <div class="col-12">
        <Pro />
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <EvolutionCompact />
      </div>
    </div>

    <div class="row">
      <div class="col-12 mb-3">
        <Binary />
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 col-lg-6 mb-3">
        <fieldset class="card-h-fix">
          <legend>
            {{ t('Graduation') }} <sup class="font-weight-light">{{ this.$moment().format('MM/YYYY') }}</sup>
          </legend>
          <div class="d-flex justify-content-center card-h-fix">
            <ChartGraduation />
          </div>
        </fieldset>
      </div>
      <div class="col-md-12 col-lg-6 mb-3">
        <fieldset class="card-h-fix">
          <legend>
            {{ t('Bonuses') }} <sup class="font-weight-light">{{ this.$moment().format('MM/YYYY') }}</sup>
          </legend>
          <div class="d-flex justify-content-center card-h-fix">
            <ChartBonus />
          </div>
        </fieldset>
      </div>
      <div class="col-md-12 col-lg-6 mb-3">
        <fieldset class="card-h-fix">
          <legend>
            {{ t('Points') }} <sup class="font-weight-light">{{ this.$moment().format('MM/YYYY') }}</sup>
          </legend>
          <div class="d-flex justify-content-center card-h-fix">
            <ChartPoints />
          </div>
        </fieldset>
      </div>
      <div class="col-md-12 col-lg-6 mb-3">
        <fieldset class="card-h-fix">
          <legend>
            {{ t('Orders') }} <sup class="font-weight-light">{{ this.$moment().format('MM/YYYY') }}</sup>
          </legend>
          <div class="d-flex justify-content-center card-h-fix">
            <ChartOrders />
          </div>
        </fieldset>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.home-graduation-row {
  gap: 30px;
  align-items: stretch;
}

.home-graduation-col,
.home-stats-col {
  flex: 0 0 100%;
  max-width: 100%;
}

@media (min-width: 768px) {
  .home-graduation-col,
  .home-stats-col {
    flex: 0 0 calc(50% - 15px);
    max-width: calc(50% - 15px);
  }

  .home-graduation-col {
    display: flex;
  }
}

@media (min-width: 1025px) {
  .home-graduation-col {
    flex: 0 0 calc(33.3333% - 15px);
    max-width: calc(33.3333% - 15px);
  }

  .home-stats-col {
    flex: 0 0 calc(66.6667% - 15px);
    max-width: calc(66.6667% - 15px);
  }
}

.card-graduation {
  width: 100%;
  aspect-ratio: 1 / 1;
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
  overflow: hidden;
}

.card-graduation.skeleton-pulse {
  background-color: #e0e0e0;
  animation: graduation-skeleton-pulse 1.4s ease-in-out infinite;
}

@keyframes graduation-skeleton-pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

@media (max-width: 1025px) {
  .card-graduation {
    height: 427px;
  }
}

@media (max-width: 769px) {
  .card-graduation {
    height: 410px;
  }
}

@media (min-width: 1026px) {
  .card-graduation {
    height: 427px;
  }
}

.card-graduation .graduation-value {
  font-size: clamp(0.85rem, 4vw, 1.25rem);
  white-space: nowrap;
}

.card-graduation .graduation-label {
  font-size: clamp(0.65rem, 2.5vw, 0.75rem);
  white-space: nowrap;
}

.card-graduation .reference-month {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 13px;
  color: #fff;
  background: rgba(10, 8, 5, 0.45);
  border-radius: 5px;
  padding: 5px 10px;
}

.story-download-toggle {
  position: absolute;
  top: 10px;
  left: 10px;
  color: #fff;
  background: rgba(10, 8, 5, 0.45);
  border-radius: 5px;
  padding: 5px 8px;
  cursor: pointer;
  line-height: 1;
}

.story-download-toggle:hover {
  background: rgba(10, 8, 5, 0.65);
}

.story-download-toggle i {
  font-size: 16px;
}

.value-toggle {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  line-height: 1;
}

.value-toggle:hover {
  color: rgba(255, 255, 255, 0.9);
}

.value-toggle i {
  font-size: 16px;
}

.value-hidden {
  filter: blur(6px);
  user-select: none;
}

.card-glass {
  background: rgba(10, 8, 5, 0.45);

  border: 1px solid rgba(255, 215, 130, 0.25);

  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);

  box-shadow:
      0 10px 30px rgba(0, 0, 0, 0.35),
      inset 0 1px 0 rgba(255, 240, 190, 0.15);
}

.story-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  border: 1px solid #e2e5ec;
  border-radius: 8px;
  background: #f8f9fc;
}

.story-summary-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #28a745;
  color: #fff;
}

.story-summary-icon i {
  font-size: 18px;
}

.story-summary-name {
  font-weight: 600;
  color: #2c2c2c;
}

.story-summary-location {
  font-size: 13px;
  color: #8a8a8a;
}
</style>